import React from 'react';
import {
  AppDateFormat,
  formatAppDate,
  getAppDateParts,
  toGregorianDateOnly,
} from '../../utils/dateDisplay';

interface DateDisplayProps {
  value: unknown;
  mode?: AppDateFormat;
  showTime?: boolean;
  dualLayout?: 'stacked' | 'inline';
  dualSeparator?: string;
  className?: string;
  gregorianClassName?: string;
  hijriClassName?: string;
  title?: string;
}

/** عرض التاريخ: ميلادي في الأعلى، هجري تحته — أو في سطر واحد */
const DateDisplay: React.FC<DateDisplayProps> = ({
  value,
  mode = 'dual',
  showTime = false,
  dualLayout = 'stacked',
  dualSeparator = ' - ',
  className = '',
  gregorianClassName = 'text-sm text-gray-900 leading-tight',
  hijriClassName = 'text-xs text-gray-500 leading-tight mt-0.5',
  title,
}) => {
  if (value == null || value === '') {
    return <span className={className}>—</span>;
  }

  if (mode !== 'dual') {
    const text = formatAppDate(value, mode);
    if (!text) return <span className={className}>—</span>;
    return (
      <span className={className} title={title}>
        {text}
      </span>
    );
  }

  const parts = getAppDateParts(value);

  if (!parts) {
    const text = formatAppDate(value, 'gregorian');
    if (!text) return <span className={className}>—</span>;
    return (
      <span className={className} title={title}>
        {text}
      </span>
    );
  }

  const d = value instanceof Date ? value : new Date(value as string);
  const time =
    showTime && !Number.isNaN(d.getTime())
      ? d.toLocaleTimeString('ar-SA-u-nu-latn', {
          hour: '2-digit',
          minute: '2-digit',
        })
      : '';

  const dateTitle =
    title ||
    (toGregorianDateOnly(value)
      ? `${parts.gregorian}${parts.hijri ? `${dualSeparator}${parts.hijri}` : ''}`
      : undefined);

  if (dualLayout === 'inline') {
    return (
      <span
        className={`inline-flex flex-wrap items-baseline gap-x-0 leading-snug ${className}`.trim()}
        title={dateTitle}
      >
        <span className={gregorianClassName}>{parts.gregorian}</span>
        {parts.hijri ? (
          <>
            <span className={gregorianClassName}>{dualSeparator}</span>
            <span className={hijriClassName}>{parts.hijri}</span>
          </>
        ) : null}
        {time ? (
          <span className="text-xs text-gray-600 leading-tight ms-1">{time}</span>
        ) : null}
      </span>
    );
  }

  return (
    <span
      className={`inline-flex flex-col items-start gap-0.5 leading-snug ${className}`.trim()}
      title={dateTitle}
    >
      <span className={gregorianClassName}>{parts.gregorian}</span>
      {parts.hijri ? <span className={hijriClassName}>{parts.hijri}</span> : null}
      {time ? (
        <span className="text-xs text-gray-600 leading-tight mt-0.5">{time}</span>
      ) : null}
    </span>
  );
};

export default DateDisplay;
